import { concatAST } from 'graphql';

/**
 * This loads all fragments and exports them as a single compiled document. This mostly exists to
 * support the creation of Apollo Client's fragment registry through createFragmentRegistry(). This
 * allows us to register all fragments at once, and lets Apollo Client handle including the
 * necessary fragments based on the specific query.
 *
 * This can't rely on filesystem globbing because it needs to operate in the browser, so a big
 * import is the way.
 *
 * If you define a new fragment, reference it here. If you don't, Apollo will let you know as soon
 * as you reference it in the query with:
 *
 * > Invariant Violation: No fragment named `X`
 *
 * NB: I wanted to make it work using require.context() and webpack bundling, but it doesn't work
 * for jest. This approach is verbose but easily managed and works for both the browser and jest.
 */
import AppInstanceInfoFragment from './AppInstanceInfoFragment.gql';
import ApplicationIdentityFragment from './ApplicationIdentityFragment.gql';
import ApplicationInstanceFragment from './ApplicationInstanceFragment.gql';
import ApplicationStationLinksFragment from './ApplicationStationLinksFragment.gql';
import ApplicationStationMatchWithAppInstanceFragment from './ApplicationStationMatchWithAppInstanceFragment.gql';
import ApplicationStationsPageFragment from './ApplicationStationsPageFragment.gql';
import ArtistFragment from './ArtistFragment.gql';
import AudioFileAvailabilityFragment from './AudioFileAvailabilityFragment.gql';
import AudioFileAvailabilityPreviewFragment from './AudioFileAvailabilityPreviewFragment.gql';
import AvailabilityAdjustmentsLogFragment from './AvailabilityAdjustmentsLogFragment.gql';
import AvailabilityAdjustmentsResultFragment from './AvailabilityAdjustmentsResultFragment.gql';
import BlipDetailsFragment from './BlipDetailsFragment.gql';
import CalculateRightsResultFragment from './CalculateRightsResultFragment.gql';
import CatalogSearchAudioFileFragment from './CatalogSearchAudioFileFragment.gql';
import CatalogSearchBlipFragment from './CatalogSearchBlipFragment.gql';
import ContentSourceDetailsFragment from './ContentSourceDetailsFragment.gql';
import ContentSourceRightsMappingFragment from './ContentSourceRightsMappingFragment.gql';
import CountryAvailabilityFragment from './CountryAvailabilityFragment.gql';
import CountryDateFragment from './CountryDateFragment.gql';
import CountryEnFragment from './CountryEnFragment.gql';
import DetailedAudioFileFragment from './DetailedAudioFileFragment.gql';
import GenreFragment from './GenreFragment.gql';
import IAdjustmentFragment from './IAdjustmentFragment.gql';
import LinkableApplicationFragment from './LinkableApplicationFragment.gql';
import MetabaseViewFragment from './MetabaseViewFragment.gql';
import PaginatedStation from './PaginatedStation.gql';
import ReleaseFragment from './ReleaseFragment.gql';
import ReportFragment from './ReportFragment.gql';
import ReportLinkFragment from './ReportLinkFragment.gql';
import SubmittedReportFragment from './SubmittedReportFragment.gql';
import SubmittedReportFileFragment from './SubmittedReportFileFragment.gql';
import RightsHistoryAdjustmentsLogFragment from './RightsHistoryAdjustmentsLogFragment.gql';
import RightsHistoryFragment from './RightsHistoryFragment.gql';
import SimpleApplicationInstanceSetFragment from './SimpleApplicationInstanceSetFragment.gql';
import SimpleAudioFileFragment from './SimpleAudioFileFragment.gql';
import SimpleBlipFragment from './SimpleBlipFragment.gql';
import SimpleCountryFragment from './SimpleCountryFragment.gql';
import SimpleStationFragment from './SimpleStationFragment.gql';
import SimpleStationWithAppFragment from './SimpleStationWithAppFragment.gql';
import StationDetails from './StationDetails.gql';
import StationDetailsAndDirtyAvailability from './StationDetailsAndDirtyAvailability.gql';
import StationDetailsFragment from './StationDetailsFragment.gql';
import StationDetailsV2Fragment from './StationDetailsV2Fragment.gql';
import StationRequestDetailFragment from './StationRequestDetailFragment.gql';
import StationRequestSummaryFragment from './StationRequestSummaryFragment.gql';
import StreamingRightsCollectionFragment from './StreamingRightsCollectionFragment.gql';
import StreamingRightsFragment from './StreamingRightsFragment.gql';
import TerritoryFragment from './TerritoryFragment.gql';
import TrackSearchFragment from './TrackSearchFragment.gql';
import TranscodeFragment from './TranscodeFragment.gql';
import UserApplicationsFragment from './UserApplicationsFragment.gql';
import UserIdentityFragment from './UserIdentityFragment.gql';
import UserPermissionsFragment from './UserPermissionsFragment.gql';
import ReportFormatterFragment from './ReportFormatterFragment.gql';

const allFragments = [
  AppInstanceInfoFragment,
  ApplicationIdentityFragment,
  ApplicationInstanceFragment,
  ApplicationStationLinksFragment,
  ApplicationStationMatchWithAppInstanceFragment,
  ApplicationStationsPageFragment,
  ArtistFragment,
  AudioFileAvailabilityFragment,
  AudioFileAvailabilityPreviewFragment,
  AvailabilityAdjustmentsLogFragment,
  AvailabilityAdjustmentsResultFragment,
  BlipDetailsFragment,
  CalculateRightsResultFragment,
  CatalogSearchAudioFileFragment,
  CatalogSearchBlipFragment,
  ContentSourceDetailsFragment,
  ContentSourceRightsMappingFragment,
  CountryAvailabilityFragment,
  CountryDateFragment,
  CountryEnFragment,
  DetailedAudioFileFragment,
  ReportFormatterFragment,
  GenreFragment,
  IAdjustmentFragment,
  LinkableApplicationFragment,
  MetabaseViewFragment,
  PaginatedStation,
  ReleaseFragment,
  ReportFragment,
  ReportLinkFragment,
  SubmittedReportFragment,
  SubmittedReportFileFragment,
  RightsHistoryAdjustmentsLogFragment,
  RightsHistoryFragment,
  SimpleApplicationInstanceSetFragment,
  SimpleAudioFileFragment,
  SimpleBlipFragment,
  SimpleCountryFragment,
  SimpleStationFragment,
  SimpleStationWithAppFragment,
  StationDetails,
  StationDetailsAndDirtyAvailability,
  StationDetailsFragment,
  StationDetailsV2Fragment,
  StationRequestDetailFragment,
  StationRequestSummaryFragment,
  StreamingRightsCollectionFragment,
  StreamingRightsFragment,
  TerritoryFragment,
  TrackSearchFragment,
  TranscodeFragment,
  UserApplicationsFragment,
  UserIdentityFragment,
  UserPermissionsFragment,
];

export default concatAST( allFragments );
